import { useContext } from "react";
import { motion } from "framer-motion";
import { BookOpenIcon, RefreshIcon } from "@heroicons/react/solid";

import { BASIC_ANIMATION_VARIANTS, NO_ACCESS_TYPES, PERMISSIONS } from "../../../../utils/constants";
import { DashboardContext } from "../../../../context/DashboardContext";

import Alert, { ALERT_TYPE } from "../../../../components/Auth/Alert";
import Assessment from "./Assessment";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import AccessControl from "../../../SharedComponents/AccessControl";
import Button from "../../../SharedComponents/Button";
import BouncingBalls from "../../../SharedComponents/Loading/BouncingBalls";
import PaginatedSearch from "../../../SharedComponents/PaginatedSearch";

function Assessments() {
    const context = useContext(DashboardContext);

    return (
        <AccessControl requiredPermission={PERMISSIONS.ACCESS_ASSESSMENTS} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer
                title={"Assessments"}
                titleIcon={<BookOpenIcon className="block h-5 w-5 mr-2" />}
                extraElements={
                    <Button
                        onClick={context.fetchAssessments}
                        text={"Refresh assessments"}
                        icon={<RefreshIcon className="block h-4 w-4 mr-2" />}
                        iconPositionLeft
                        extraStyling={
                            "text-white focus:ring-inset focus:ring-1 bg-primary-600 " +
                            "hover:bg-primary-600 focus:outline-none focus:ring-primary-500"
                        }
                        loading={context.assessmentsLoading}
                    />
                }
            >
                <div className="flex flex-col gap-4 mb-6">
                    <Alert
                        text={
                            "If you have been assigned an assessment recently and you don't see it listed, please " +
                            "note that it might take a few minutes to appear. Use the button above to refresh this " +
                            "list. If the problem persists contact your program manager or " +
                            "techsupport@psychpress.com.au."
                        }
                        type={ALERT_TYPE.NEUTRAL}
                        active={true}
                        marginLess
                    />
                    <Alert
                        text={
                            "Please allow pop-ups on your browser (if prompted) by clicking 'Allow' to open " +
                            "Assessments. If you are having issues please check browser pop-up permissions for " +
                            "this website."
                        }
                        type={ALERT_TYPE.NEUTRAL}
                        active={true}
                        marginLess
                    />
                </div>
                {context.userAssessments && context.userAssessments.length > 0 ? (
                    <motion.div
                        initial={"inactive"}
                        animate={"active"}
                        exit={"inactive"}
                        transition={{ duration: 0.4 }}
                        variants={BASIC_ANIMATION_VARIANTS}
                    >
                        <PaginatedSearch
                            loading={context.assessmentsLoading}
                            items={context.userAssessments}
                            itemName={"Assessments"}
                            searchAttributes={["name", "type", "dueDate"]}
                            ItemRow={Assessment}
                            cards
                        />
                    </motion.div>
                ) : context.userAssessments ? (
                    <motion.div
                        initial={"inactive"}
                        animate={"active"}
                        exit={"inactive"}
                        transition={{ duration: 0.4 }}
                        variants={BASIC_ANIMATION_VARIANTS}
                    >
                        <div>
                            Your haven't been assigned any assessments. If you think this is incorrect, please contact
                            us at{" "}
                            <a
                                className="link text-primary-600 hover:text-primary-700"
                                href={
                                    "mailto:techsupport@psychpress.com.au?subject=I cannot see the assessments I" +
                                    "have been assigned."
                                }
                            >
                                techsupport@psychpress.com.au
                            </a>{" "}
                            or call us at{" "}
                            <a className="link text-primary-600 hover:text-primary-700" href="tel:+61396700590">
                                +61 (0)3 9670 0590
                            </a>{" "}
                            for more information.
                        </div>
                    </motion.div>
                ) : (
                    <Alert type={ALERT_TYPE.WARNING} active={context.userAssessments == null}>
                        Oops! It looks like we are having some trouble retrieving your assessments. We are so sorry - we
                        are working on it. If you require immediate assistance, please contact us at{" "}
                        <a
                            className="link"
                            href="mailto:techsupport@psychpress.com.au?subject=The assessments server is down."
                        >
                            techsupport@psychpress.com.au
                        </a>{" "}
                        or call us at{" "}
                        <a className="link text-primary-600 hover:text-primary-700" href="tel:+61396700590">
                            +61 (0)3 9670 0590
                        </a>
                    </Alert>
                )}
            </DashboardContainer>
        </AccessControl>
    );
}

export default Assessments;
